import React, { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useGetLeadCountriesQuery } from "../../../redux/services/crmApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  setCrmLeadsSourceCountry,
  clearCrmLeadsSourceCountry,
  selectCrmLeadsFilters,
  clearCrmLeadsDestinationCountry,
  setCrmLeadsDestinationCountry,
  selectCommunicationsFilters,
  setCommunicationsDestinationCountry,
  clearCommunicationsDestinationCountry,
  clearCommunicationsUniversity,
  clearCrmLeadsDestinationCity,
  clearCrmLeadsUniversity,
  clearCommunicationsCity,
  clearCommunicationsSourceCountry,
  setCommunicationsSourceCountry,
} from "../../../redux/slices/filterSlice";
import {
  setPmsDestinationCountry,
  clearPmsDestinationCountry,
  clearPmsCity,
  clearPmsUniversity,
  selectPmsPropertiesFilters,
} from "../../../redux/slices/pmsFilterSlice";
import {
  selectTasksFilters,
  setTasksCountryIds,
  clearTasksCityIds,
} from "../../../redux/slices/tasksFilterSlice";
import { useDispatch, useSelector } from "react-redux";
import FlagIconCDN from "../../FlagIconCDN/FlagIconCDN";

interface Country {
  id: string;
  name: string;
  alpha_2: string;
}

const CountryFilter = ({
  onClose,
  isDestinationCountry = false,
  isCommunicationDestCountry = false,
  isCommunicationSourceCountry = false,
  isMultiSelect = false,
  isCrmSourceCountry = false,
  isPmsDestCountry = false,
  isCrmDestinationCountry = false,
  isTasks = false,
}: {
  onClose: () => void;
  isDestinationCountry?: boolean;
  isCommunicationDestCountry?: boolean;
  isCommunicationSourceCountry?: boolean;
  isMultiSelect?: boolean;
  isCrmSourceCountry?: boolean;
  isPmsDestCountry?: boolean;
  isCrmDestinationCountry?: boolean;
  isTasks?: boolean;
}) => {
  const leadsFilters = useSelector(selectCrmLeadsFilters);
  const communicationsFilters = useSelector(selectCommunicationsFilters);
  const pmsPropertiesFilters = useSelector(selectPmsPropertiesFilters);
  const tasksFilters = useSelector(selectTasksFilters);
  const selectedCountry = isTasks
    ? tasksFilters?.countryIds
    : isPmsDestCountry
    ? pmsPropertiesFilters?.destinationCountry
    : isCommunicationDestCountry
    ? communicationsFilters?.destinationCountry
    : isCommunicationSourceCountry
    ? communicationsFilters?.sourceCountry
    : isDestinationCountry
    ? leadsFilters?.destinationCountry
    : leadsFilters?.sourceCountry;

  const dispatch = useDispatch();

  const {
    data: countries,
    isLoading,
    isFetching,
  } = useGetLeadCountriesQuery({});

  const transformedCountries = useMemo(
    () =>
      countries?.map((country: Country) => ({
        value: country.id,
        label: country.name,
        flag: country.alpha_2,
      })) || [],
    [countries]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCountries, setFilteredCountries] = useState<
    SearchableDropdownOption[]
  >([]);

  // Helper to get single value from selectedCountry (handles both string and array formats)
  const getSingleValue = (val: string | string[] | undefined): string => {
    if (!val) return "";
    if (Array.isArray(val)) return val[0] || "";
    return val;
  };

  // Single select state
  const [selectedCountryId, setSelectedCountryId] = useState<{
    value: string;
    displayValue: string;
  }>({
    value: !isMultiSelect ? getSingleValue(selectedCountry.value as string | string[]) : "",
    displayValue: !isMultiSelect ? getSingleValue(selectedCountry.displayValue as string | string[]) : "",
  });

  // Multi select state
  const [selectedCountryIds, setSelectedCountryIds] = useState<
    Array<{ value: string; displayValue: string; flag: string }>
  >(
    isMultiSelect && Array.isArray(selectedCountry.value)
      ? (selectedCountry.value as string[]).map((id, index) => {
          const flag = isCommunicationSourceCountry
            ? id
            : transformedCountries?.find(
                (c: SearchableDropdownOption) => c.value === id
              )?.flag || "";

          return {
            value: id,
            displayValue:
              (selectedCountry.displayValue as string[])[index] || "",
            flag: flag,
          };
        })
      : []
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredCountries(transformedCountries);
        return;
      }

      const filtered = transformedCountries.filter(
        (country: SearchableDropdownOption) =>
          country.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCountries(filtered);
    },
    [transformedCountries]
  );

  // Debounced search
  useEffect(() => {
    handleSearch(searchQuery);
  }, [searchQuery, handleSearch]);

  // Initialize filtered countries
  useEffect(() => {
    setFilteredCountries(transformedCountries);
  }, [transformedCountries]);

  const handleCountrySelect = (
    countryId: string,
    countryLabel: string,
    countryFlag: string
  ) => {
    // For communication source country, use alpha_2 (countryFlag) as value
    const valueToStore = isCommunicationSourceCountry || isCrmSourceCountry ? countryFlag : countryId;

    if (isMultiSelect) {
      setSelectedCountryIds((prevCountries) => {
        const isAlreadySelected = prevCountries.some(
          (c) => c.value === valueToStore
        );
        if (isAlreadySelected) {
          return prevCountries.filter((c) => c.value !== valueToStore);
        }
        return [
          ...prevCountries,
          {
            value: valueToStore,
            displayValue: countryLabel,
            flag: countryFlag,
          },
        ];
      });
    } else {
      if (valueToStore === selectedCountryId.value) {
        setSelectedCountryId({ value: "", displayValue: "" });
        dispatch(
          isCommunicationDestCountry
            ? clearCommunicationsDestinationCountry()
            : isCommunicationSourceCountry
            ? clearCommunicationsSourceCountry()
            : isDestinationCountry
            ? clearCrmLeadsDestinationCountry()
            : clearCrmLeadsSourceCountry()
        );
        onClose();
      } else {
        setSelectedCountryId({
          value: valueToStore,
          displayValue: countryLabel,
        });
      }
    }
  };

  const handleCancel = () => {
    if (isMultiSelect) {
      setSelectedCountryIds([]);
    } else {
      setSelectedCountryId({ value: "", displayValue: "" });
    }
    setSearchQuery("");
    onClose();
  };

  const handleApply = () => {
    if (isMultiSelect) {
      const countryValues = selectedCountryIds.map((c) => c.value);
      const countryDisplayValues = selectedCountryIds.map(
        (c) => c.displayValue
      );

      // Multi-select is only for communications
      if (isCommunicationSourceCountry) {
        dispatch(
          setCommunicationsSourceCountry({
            value: countryValues,
            displayValue: countryDisplayValues,
          })
        );
      }
      if (isCommunicationDestCountry) {
        dispatch(
          setCommunicationsDestinationCountry({
            value: countryValues,
            displayValue: countryDisplayValues,
          })
        );
        dispatch(clearCommunicationsCity());
        dispatch(clearCommunicationsUniversity());
      }
      if (isCrmDestinationCountry) {
        dispatch(
          setCrmLeadsDestinationCountry({
            value: countryValues,
            displayValue: countryDisplayValues,
          })
        );
        dispatch(clearCrmLeadsDestinationCity());
        dispatch(clearCrmLeadsUniversity());
      }

      if (isCrmSourceCountry) {
        dispatch(
          setCrmLeadsSourceCountry({
            value: countryValues,
            displayValue: countryDisplayValues,
          })
        );
      }
      if (isTasks) {
        dispatch(
          setTasksCountryIds({
            value: countryValues,
            displayValue: countryDisplayValues,
          })
        );
        dispatch(clearTasksCityIds());
      }
    } else {
      if (selectedCountryId.value === getSingleValue(selectedCountry.value as string | string[])) {
        return;
      }

      if (isPmsDestCountry) {
        dispatch(
          setPmsDestinationCountry({
            id: selectedCountryId.value ? Number(selectedCountryId.value) : null,
            value: selectedCountryId.value as string,
            displayValue: selectedCountryId.displayValue as string,
          })
        );
        dispatch(clearPmsCity());
        dispatch(clearPmsUniversity());
      } else if (isCommunicationDestCountry) {
        dispatch(
          setCommunicationsDestinationCountry({
            value: [selectedCountryId.value as string],
            displayValue: [selectedCountryId.displayValue as string],
          })
        );
        dispatch(clearCommunicationsCity());
        dispatch(clearCommunicationsUniversity());
      // } else if (isDestinationCountry) {
      //   dispatch(
      //     setCrmLeadsDestinationCountry({
      //       value: selectedCountryId.value as string,
      //       displayValue: selectedCountryId.displayValue as string,
      //     })
      //   );
      //   dispatch(clearCrmLeadsDestinationCity());
      //   dispatch(clearCrmLeadsUniversity());
      // } else {
      //   dispatch(
      //     setCrmLeadsSourceCountry({
      //       value: selectedCountryId.value as string,
      //       displayValue: selectedCountryId.displayValue as string,
      //     })
      //   );
      // }
    }
  }
    onClose();
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search country"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Multi-Select: Selected Count */}
      {isMultiSelect && (
        <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1 shrink-0">
          SELECTED ({selectedCountryIds?.length ?? 0})
        </span>
      )}

      {/* Multi-Select: Selected Countries Display */}
      {isMultiSelect && selectedCountryIds.length > 0 && (
        <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk max-h-[300px] overflow-y-auto shrink-0">
          {selectedCountryIds.map((country) => {
            // Find country by alpha_2 for communication source country, otherwise by id
            const matchingCountry = isCommunicationSourceCountry || isCrmSourceCountry
              ? transformedCountries?.find(
                  (c: SearchableDropdownOption) => c?.flag === country.value
                )
              : transformedCountries?.find(
                  (c: SearchableDropdownOption) => c?.value === country.value
                );

            return (
              <div
                key={country.value}
                onClick={() => {
                  // Pass the original country.id for communication source, not the alpha_2
                  const countryIdToPass = isCommunicationSourceCountry || isCrmSourceCountry
                    ? matchingCountry?.value || country.value 
                    : country.value;

                  handleCountrySelect(
                    countryIdToPass,
                    country.displayValue,
                    matchingCountry?.flag || country.flag || country.value
                  );
                }}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <FlagIconCDN
                    code={
                      matchingCountry?.flag || country.flag || country.value
                    }
                    flagIconClassName="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {country.displayValue}
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Single-Select: Selected Country Display */}
      {!isMultiSelect && selectedCountryId.value && (
        <div
          className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk shrink-0 cursor-pointer"
          onClick={() => setSelectedCountryId({ value: "", displayValue: "" })}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk flex items-center gap-2">
              <FlagIconCDN
                code={
                  isCommunicationSourceCountry || isCrmSourceCountry
                    ? selectedCountryId.value // For communication source, value is already alpha_2
                    : transformedCountries?.find(
                        (c: SearchableDropdownOption) =>
                          c?.value === selectedCountryId?.value
                      )?.flag || ""
                }
                flagIconClassName="w-4 h-4"
              />
              {isCommunicationSourceCountry || isCrmSourceCountry
                ? transformedCountries.find(
                    (c: SearchableDropdownOption) =>
                      c?.flag === selectedCountryId?.value
                  )?.label || selectedCountryId.displayValue
                : transformedCountries.find(
                    (c: SearchableDropdownOption) =>
                      c?.value === selectedCountryId?.value
                  )?.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Countries List */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk shrink-0">
          All Countries
        </div>

        <div className="overflow-y-auto flex-1">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading countries..." : "Fetching countries..."}
              </div>
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim()
                  ? "No countries found"
                  : "No countries available"}
              </div>
            </div>
          ) : (
            filteredCountries
              ?.filter((country: SearchableDropdownOption) => {
                const valueToCheck = isCommunicationSourceCountry || isCrmSourceCountry
                  ? country.flag
                  : country.value;
                return isMultiSelect
                  ? !selectedCountryIds.some((c) => c.value === valueToCheck)
                  : valueToCheck !== selectedCountryId.value;
              })
              ?.map((country: SearchableDropdownOption) => (
                <div
                  key={country.value}
                  onClick={() =>
                    handleCountrySelect(
                      country.value,
                      country.label as string,
                      country.flag || ""
                    )
                  }
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FlagIconCDN
                        code={country.flag || ""}
                        flagIconClassName="w-4 h-4"
                      />
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {country.label}
                      </div>
                    </div>
                  </div>
                  {isMultiSelect ? (
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  ) : (
                    selectedCountryId.value === country.value && (
                      <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                    )
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2 shrink-0 pt-4">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={
            isMultiSelect
              ? selectedCountryIds.length === 0
              : isCommunicationDestCountry
              ? Array.isArray(selectedCountry.value) &&
                selectedCountry.value.length === 1 &&
                selectedCountry.value[0] === selectedCountryId.value
              : selectedCountryId.value === getSingleValue(selectedCountry.value as string | string[])
          }
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CountryFilter;
